import type { NextPage } from 'next';
import getConfig from 'next/config';
import Router, { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { TCreateReviewPageQuery } from 'src/types/router';

import { AntiFraudSession } from '@sravni/mpl-antifraud/lib/types/antiFraudSession';
import { Layout } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import { GetBannersParams } from '../../__generated__/gateway/gatewayApiTypes';
import { AntiFraud } from '../../components/AntiFraud';
import { CreateReview } from '../../components/CreateReview';
import { getReviewType } from '../../helpers/reviews';
import { makeCreateReviewMetadata } from '../../helpers/seo';
import { TBannerTypes } from '../../interfaces/advertising';
import { useProductsTags } from '../../store/hooks/filters';
import { fetchBanners } from '../../store/reducers/advertising';
import { setMetadata } from '../../store/reducers/metadata';
import { setOrganizationByAlias, setOrganizationsList } from '../../store/reducers/organizations';
import { setReviewItem } from '../../store/reducers/reviews';
import { selectCurrentReviewType, selectReviewItem } from '../../store/selectors/reviews';
import { serverDataDispatcher } from '../../utils/serverDataDispatcher';

type TProps = {
  afSession: AntiFraudSession;
  isEdit: boolean;
};

const CreatePage: NextPage<TProps> = ({ afSession, isEdit }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const router = useRouter();
  const query = (router.query as unknown) as TCreateReviewPageQuery;
  const productsTags = useProductsTags();
  const reviewTypes = getReviewType(query.type, productsTags);

  useEffect(() => {
    dispatch(
      fetchBanners({
        params: {
          Category: reviewTypes.dfpCategory,
          Page: 'reviews' as GetBannersParams['Page'],
          BannerTypes: ['top'] as TBannerTypes[],
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {getConfig().publicRuntimeConfig.antiFraud && <AntiFraud session={afSession} />}
      <Layout className={isMobile ? 'h-mt-24 h-mb-24' : 'h-mt-32 h-mb-32'}>
        <CreateReview isEdit={isEdit} />
      </Layout>
    </>
  );
};

CreatePage.getInitialProps = async (ctx: App.ReduxNextPageContext<TCreateReviewPageQuery>) => {
  const {
    store: { dispatch, getState },
    asPath,
    query: { id },
  } = ctx;

  const [pathname] = asPath.split('?');

  dispatch(
    serverDataDispatcher(ctx.req, {
      __ORGANIZATIONS__: setOrganizationsList,
      __ORGANIZATION__: setOrganizationByAlias,
      __REVIEW__: setReviewItem,
    }),
  );

  const { item } = selectReviewItem(getState());

  const isEdit = item?.id && pathname.includes('edit');

  if (id && !item?.canEditReview && !item?.canEditReviewHiddenFields) {
    if (ctx?.res) {
      ctx.res?.writeHead(301, {
        Location: `${ctx.req.url.replace('/edit', '')}`,
      });
      ctx.res?.end();
    } else if (ctx?.asPath) {
      await Router.replace(ctx?.asPath.replace('/edit', ''));
    }
  }

  const reviewType = selectCurrentReviewType(getState());
  const seoData = makeCreateReviewMetadata(reviewType);
  dispatch(setMetadata({ data: seoData, pathname }));

  return {
    afSession: ctx.req.__ANTI_FRAUD_SESSION__,
    isEdit,
  };
};

export default CreatePage;
